(function() {
  // Rough, best-effort "is this an old/underpowered device" signal, used to
  // trim visual/rendering cost further than screen-width-based mobile
  // tiering alone can. deviceMemory and hardwareConcurrency are Chromium-
  // only (most old/budget Android phones - the common "old mobile" case);
  // neither exists on iOS Safari, where this simply stays false and iPhones
  // just get the normal mobile tier. Computed once for the whole page, not
  // per section - device capability doesn't change between flipbooks.
  const isLowEndDevice = (() => {
    const mem = navigator.deviceMemory;
    const cores = navigator.hardwareConcurrency;
    if (typeof mem === 'number' && mem <= 2) return true;
    if (typeof cores === 'number' && cores <= 2) return true;
    return false;
  })();

  // ---------------------------------------------------------------------
  // Persistent page-bitmap cache (IndexedDB), shared across every flipbook
  // instance and page load. A RETURNING visitor to the same catalog skips
  // pdf.js decode+paint entirely for pages they've already viewed -
  // decoding a compressed JPEG is much cheaper than pdf.js parsing and
  // vector-painting a photographic page from scratch. Keyed on the PDF
  // URLs + page number + render scale, so mobile/desktop or a resize never
  // reuses a bitmap rendered at the wrong resolution. Time-bounded (14
  // days) since there's no cheap way to detect "this PDF was replaced at
  // the same URL" without an extra network round-trip per page; entry-
  // count-bounded so this can't grow without limit. Every failure mode
  // (IndexedDB unavailable, quota exceeded, corrupt entry) degrades to
  // "just render normally" - this is a speed-up, never a dependency.
  // ---------------------------------------------------------------------
  const PAGE_CACHE_DB = 'fbmPageCache';
  const PAGE_CACHE_STORE = 'pages';
  const PAGE_CACHE_MAX_ENTRIES = 200;
  const PAGE_CACHE_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

  let pageCacheDbPromise = null;
  function openPageCacheDb() {
    if (!window.indexedDB) return Promise.resolve(null);
    if (!pageCacheDbPromise) {
      pageCacheDbPromise = new Promise((resolve) => {
        let req;
        try {
          req = indexedDB.open(PAGE_CACHE_DB, 1);
        } catch (e) {
          resolve(null);
          return;
        }
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(PAGE_CACHE_STORE)) {
            const store = db.createObjectStore(PAGE_CACHE_STORE, { keyPath: 'key' });
            store.createIndex('timestamp', 'timestamp');
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      });
    }
    return pageCacheDbPromise;
  }

  async function getCachedPageBitmap(key) {
    const db = await openPageCacheDb();
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(PAGE_CACHE_STORE, 'readonly');
        const req = tx.objectStore(PAGE_CACHE_STORE).get(key);
        req.onsuccess = () => {
          const entry = req.result;
          if (!entry || (Date.now() - entry.timestamp) > PAGE_CACHE_MAX_AGE_MS) {
            resolve(null);
            return;
          }
          resolve(entry.blob);
        };
        req.onerror = () => resolve(null);
      } catch (e) {
        resolve(null);
      }
    });
  }

  async function setCachedPageBitmap(key, blob) {
    const db = await openPageCacheDb();
    if (!db) return;
    try {
      const tx = db.transaction(PAGE_CACHE_STORE, 'readwrite');
      tx.objectStore(PAGE_CACHE_STORE).put({ key, blob, timestamp: Date.now() });
      // Eviction is a maintenance sweep, not needed on every single write.
      if (Math.random() < 0.1) evictOldPageCacheEntries(db);
    } catch (e) {
      // Quota exceeded or storage disabled - just skip caching this page.
    }
  }

  function evictOldPageCacheEntries(db) {
    try {
      const countTx = db.transaction(PAGE_CACHE_STORE, 'readonly');
      const countReq = countTx.objectStore(PAGE_CACHE_STORE).count();
      countReq.onsuccess = () => {
        const excess = countReq.result - PAGE_CACHE_MAX_ENTRIES;
        if (excess <= 0) return;
        const delTx = db.transaction(PAGE_CACHE_STORE, 'readwrite');
        const index = delTx.objectStore(PAGE_CACHE_STORE).index('timestamp');
        let deleted = 0;
        const cursorReq = index.openCursor(); // ascending by timestamp = oldest first
        cursorReq.onsuccess = (e) => {
          const cursor = e.target.result;
          if (!cursor || deleted >= excess) return;
          cursor.delete();
          deleted++;
          cursor.continue();
        };
      };
    } catch (e) {
      // Best-effort maintenance - a failed sweep just means the cache
      // stays a bit over its soft cap until the next successful one.
    }
  }

  // ---------------------------------------------------------------------
  // Shared loader: dedupes CDN script loads across every flipbook section
  // on the page (no more double-loading pdf.js/page-flip/pdf-lib when a
  // page has more than one flipbook), lazy-loads pdf-lib only when the
  // user actually clicks Download/Print, and preconnects to the CDN
  // origins right before use.
  // ---------------------------------------------------------------------
  window.__fbmLoader = window.__fbmLoader || {
    scripts: {},
    preconnected: {},
    // Loaded from the public CDNs, not self-hosted: cdnjs/jsdelivr/unpkg
    // serve these exact files to a huge share of the web, so they're
    // pre-warmed at essentially every edge node globally. A freshly
    // uploaded Shopify-hosted copy has no such warm-up - the first
    // requests can hit a cold edge that fetches from origin, which for a
    // ~1MB+ payload outweighed the DNS/TLS savings self-hosting was
    // supposed to buy. Measured slower in practice; reverted.
    preconnect(origins) {
      origins.forEach((origin) => {
        if (this.preconnected[origin]) return;
        this.preconnected[origin] = true;
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      });
    },
    loadScript(src) {
      if (this.scripts[src]) return this.scripts[src];
      this.scripts[src] = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load ' + src));
        document.head.appendChild(s);
      });
      return this.scripts[src];
    },
    loadCore() {
      if (!this.corePromise) {
        this.preconnect(['https://cdnjs.cloudflare.com', 'https://cdn.jsdelivr.net']);
        this.corePromise = Promise.all([
          this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'),
          this.loadScript('https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.min.js')
        ]).then(() => {
          const pdfjsLib = window['pdfjs-dist/build/pdf'];
          pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }).catch((err) => {
          this.corePromise = null; // allow retry
          throw err;
        });
      }
      return this.corePromise;
    },
    loadPdfLib() {
      if (!this.pdfLibPromise) {
        this.preconnect(['https://unpkg.com']);
        // Pinned to a known-stable release instead of an unversioned URL,
        // so this doesn't silently change under us on a future publish.
        this.pdfLibPromise = this.loadScript('https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js').catch((err) => {
          this.pdfLibPromise = null;
          throw err;
        });
      }
      return this.pdfLibPromise;
    }
  };

  function pushAnalyticsEvent(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: 'flipbook_interaction' }, payload));
  }

  function initFlipbook(sectionId) {
    const section = document.getElementById('fbm-' + sectionId);
    if (!section) return;
    if (section.dataset.fbmInitialized === 'true') return;
    section.dataset.fbmInitialized = 'true';

    const tabs = section.querySelectorAll('.fbm-pdf-tab');
    const viewport = section.querySelector('#fbm-viewport-' + sectionId);
    const loader = section.querySelector('#fbm-loader-' + sectionId);
    const loaderText = loader.querySelector('.fbm-loader-text');
    const retryButton = loader.querySelector('.fbm-retry-button');
    const renderArea = section.querySelector('.fbm-render-area');
    const thumbOverlay = section.querySelector('.fbm-thumbnails-overlay');
    const thumbGrid = section.querySelector('.fbm-thumb-grid');
    const thumbCloseBtn = section.querySelector('.fbm-thumb-close');
    const shareMenu = section.querySelector('.fbm-share-menu');
    const jumpMenu = section.querySelector('.fbm-jump-menu');
    const jumpInput = section.querySelector('.fbm-jump-input');
    const jumpGoBtn = section.querySelector('.fbm-jump-go');
    const searchMenu = section.querySelector('.fbm-search-menu');
    const searchInput = section.querySelector('.fbm-search-input');
    const searchCount = section.querySelector('.fbm-search-count');

    const pdfData = section.querySelector('.fbm-pdf-data');

    let pdfjsLib = null;
    let pageFlip = null;
    let pdfDocs = [];
    let pageMap = [];
    let autoplayTimer = null;
    let isAutoplay = false;
    let lastWidth = window.innerWidth;
    let lastIsMobile = window.innerWidth < 768;
    let isZoomed = false;
    const zoomScale = 2.2;

    // Populated on load, reused by resize + render-scale calculations so a
    // resize doesn't require re-fetching PDF metadata.
    let pdfPageRatio = null;
    let pdfPageWidthPts = null;
    let currentBW = 0;
    // Cached once per canvasContainer build instead of re-querying the DOM
    // (container.querySelectorAll('.fbm-page')) on every renderPage() call -
    // on a thick book that query alone re-scans every page element just to
    // reach the data-rendered early-return, up to 8x per flip.
    let pageDivs = [];

    const pageTextCache = {}; // globalPageNum -> string, used by search
    let searchMatches = [];
    let searchMatchIndex = -1;
    let searchToken = 0;
    let catalogLabel = '';
    // After several consecutive text-less pages, stop calling
    // getTextContent() on every subsequent render for this catalog - it's
    // real per-page async work (multiplied by up to 8 renders per flip)
    // that's wasted once a scanned/image-only catalog has made its answer
    // obvious. A run of misses rather than a single one, so one text-less
    // cover page in an otherwise text-bearing book doesn't wrongly turn
    // this off for the rest of it.
    let consecutiveTextlessPages = 0;
    let skipTextLayerForCatalog = false;

    // Guards against the window 'resize' listener and the fullscreen
    // handler both reacting to the same viewport change and racing each
    // other (one full reload from fullscreenchange, a lightweight resize
    // from the resize listener) - which left the book sized wrong.
    let suppressResizeReload = false;

    let activePdfUrls = [];
    let initialUrls = (tabs.length && tabs[0].dataset.pdfUrls) || (pdfData && pdfData.dataset.defaultPdf);
    if (initialUrls) activePdfUrls = initialUrls.split(',').filter(url => url.trim() !== "");
    if (tabs.length) catalogLabel = tabs[0].textContent.trim();

    function setLoaderState(state, message) {
      loader.style.display = 'flex';
      if (loaderText) loaderText.textContent = message || (state === 'error' ? 'Failed to load.' : 'Loading...');
      if (retryButton) retryButton.hidden = state !== 'error';
      const spinner = loader.querySelector('.fbm-spinner');
      if (spinner) spinner.style.display = state === 'error' ? 'none' : 'block';
    }

    // Fullscreen Logic
    function toggleFullscreen() {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (section.requestFullscreen) section.requestFullscreen();
        else if (section.webkitRequestFullscreen) section.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    }

    // Force the ENTIRE ancestor chain (.fbm-wrapper -> .fbm-main-container
    // -> .fbm-render-area) to the real viewport size via inline styles,
    // instead of relying on the .fbm-section:fullscreen CSS to stretch
    // them. Forcing render-area alone was not enough: .fbm-wrapper still
    // carries an inline max-width from the theme setting, and
    // .fbm-main-container clips with overflow:hidden - so even a wider
    // render-area was being cut back down by an ancestor that never
    // actually grew. Inline styles apply immediately with no cascade or
    // specificity to depend on. Called when fullscreen is entered and again
    // on any later resize while still in fullscreen (e.g. devtools
    // toggling), so it never goes stale.
    // The header/tab-switcher float as an overlay in fullscreen (see CSS)
    // instead of taking flex space, so the book only has to make room for
    // .fbm-controls - not the header too, and doesn't resize every time
    // the header shows/hides on hover.
    function applyFullscreenLayout() {
      const wrapperEl = section.querySelector('.fbm-wrapper');
      const mainContainerEl = section.querySelector('.fbm-main-container');
      const controlsEl = section.querySelector('.fbm-controls');

      const controlsReserved = controlsEl ? controlsEl.offsetHeight : 70;

      if (wrapperEl) {
        wrapperEl.style.maxWidth = 'none';
        wrapperEl.style.width = window.innerWidth + 'px';
        wrapperEl.style.height = window.innerHeight + 'px';
        wrapperEl.style.margin = '0';
      }
      if (mainContainerEl) {
        mainContainerEl.style.maxHeight = 'none';
        mainContainerEl.style.width = window.innerWidth + 'px';
        mainContainerEl.style.height = window.innerHeight + 'px';
      }
      // height alone isn't enough - .fbm-render-area's base CSS also sets
      // max-height:1000px (a SEPARATE property), which silently caps
      // whatever height we set here on any screen taller than that. Same
      // deal for max-width, in case a narrower cap is ever added later.
      renderArea.style.maxWidth = 'none';
      renderArea.style.maxHeight = 'none';
      renderArea.style.width = window.innerWidth + 'px';
      renderArea.style.height = Math.max(200, window.innerHeight - controlsReserved) + 'px';
    }

    function clearFullscreenLayout() {
      const wrapperEl = section.querySelector('.fbm-wrapper');
      const mainContainerEl = section.querySelector('.fbm-main-container');
      if (wrapperEl) {
        wrapperEl.style.maxWidth = '';
        wrapperEl.style.width = '';
        wrapperEl.style.height = '';
        wrapperEl.style.margin = '';
      }
      if (mainContainerEl) {
        mainContainerEl.style.maxHeight = '';
        mainContainerEl.style.width = '';
        mainContainerEl.style.height = '';
      }
      renderArea.style.maxWidth = '';
      renderArea.style.maxHeight = '';
      renderArea.style.width = '';
      renderArea.style.height = '';
    }

    // --- FULLSCREEN HEADER AUTO-HIDE (hover-reveal near the top) ---
    // Fullscreen only - the fullscreen button itself is hidden on mobile
    // (.fbm-hide-mobile), so there's no touch/tap case to cover here.
    const HEADER_HOVER_ZONE_PX = 80;
    const HEADER_HIDE_DELAY_MS = 200;
    let headerHideTimer = null;

    function showFullscreenHeader() {
      clearTimeout(headerHideTimer);
      section.classList.add('fbm-header-visible');
    }
    function scheduleFullscreenHeaderHide() {
      clearTimeout(headerHideTimer);
      headerHideTimer = setTimeout(() => {
        section.classList.remove('fbm-header-visible');
      }, HEADER_HIDE_DELAY_MS);
    }
    document.addEventListener('mousemove', (e) => {
      if (!isThisSectionFullscreen) return;
      if (e.clientY <= HEADER_HOVER_ZONE_PX) {
        showFullscreenHeader();
      } else {
        scheduleFullscreenHeaderHide();
      }
    });

    // Listener to swap icons & auto-resize.
    // Scoped to THIS section (document.fullscreenElement === section) so
    // that on a page with multiple flipbooks, only the section that
    // actually entered/exited fullscreen reloads - the others no longer
    // get dragged into an unnecessary reload.
    let isThisSectionFullscreen = false;
    const handleFSChange = () => {
      const fsBtn = section.querySelector('[data-action="fullscreen"]');
      const nowFS = document.fullscreenElement === section || document.webkitFullscreenElement === section;
      if (nowFS === isThisSectionFullscreen) return;
      isThisSectionFullscreen = nowFS;

      if (fsBtn) {
        fsBtn.querySelector('.fs-enter-icon').style.display = nowFS ? 'none' : 'block';
        fsBtn.querySelector('.fs-exit-icon').style.display = nowFS ? 'block' : 'none';
      }

      // Claim the resize handling immediately (synchronously) so the
      // window 'resize' event this same fullscreen transition triggers
      // can't sneak in its own lightweight resize/reload in between and
      // leave the book sized for the wrong state.
      suppressResizeReload = true;
      clearTimeout(window.fbResizeTimeout);

      if (nowFS) {
        applyFullscreenLayout();
        // Shown briefly on entry (so it's clear it's there at all), then
        // auto-hides on the usual delay unless the cursor is already near
        // the top.
        showFullscreenHeader();
        scheduleFullscreenHeaderHide();
      } else {
        clearFullscreenLayout();
        clearTimeout(headerHideTimer);
        section.classList.remove('fbm-header-visible');
      }

      // Re-load the PDF so sizing matches the full screen
      setTimeout(() => {
        lastWidth = window.innerWidth;
        lastIsMobile = window.innerWidth < 768;
        if (pageFlip) {
          loadPdf(activePdfUrls).finally(() => { suppressResizeReload = false; });
        } else {
          suppressResizeReload = false;
        }
      }, 300);
    };

    document.addEventListener('fullscreenchange', handleFSChange);
    document.addEventListener('webkitfullscreenchange', handleFSChange);

    // --- ZOOM LOGIC ---
    function toggleZoom(forceState) {
      if (!pageFlip) return;

      const container = section.querySelector('.fbm-canvas-container');
      const renderArea = section.querySelector('.fbm-render-area');
      const zoomBtn = section.querySelector('[data-action="zoom"]');

      if (!container) return;

      const totalPages = pageMap.length;
      const currentIndex = pageFlip.getCurrentPageIndex();

      isZoomed = typeof forceState === 'boolean' ? forceState : !isZoomed;

      // Determine resting translate based on current page
      let restingTranslate = 0;
      if (window.innerWidth >= 768) {
        if (currentIndex === 0) restingTranslate = -25;
        else if (currentIndex + 1 >= totalPages) restingTranslate = 25;
      }

      if (isZoomed) {
        // --- ZOOM ACTIVE ---
        stopAutoplay();
        renderArea.classList.add('is-zoomed');

        //Determine the starting center point of the zoom so that the cover explodes right in the center.
        let initialX = 50;
        if (window.innerWidth >= 768) {
          if (currentIndex === 0) initialX = 75; //Center point of front cover
          else if (currentIndex + 1 >= totalPages) initialX = 25; // Center point of back cover
        }

        // Set initial state without transition
        container.style.transition = 'none';
        container.style.transformOrigin = `${initialX}% 50%`;
        container.style.transform = `translateX(${restingTranslate}%) scale(1)`;

        // Force Reflow
        container.offsetHeight;

        // Animate to zoomed state
        container.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1), transform-origin 0.2s ease';
        container.classList.remove('fbm-is-cover', 'fbm-is-back-cover');
        container.style.transform = `translateX(0%) scale(${zoomScale})`;

        if (zoomBtn) {
          //zoomBtn.style.backgroundColor = "#5da9dd";
          zoomBtn.querySelector('.zoom-in-icon').style.display = 'none';
          zoomBtn.querySelector('.zoom-out-icon').style.display = 'block';
        }
      } else {
        // --- ZOOM OFF ---
        renderArea.classList.remove('is-zoomed');

        // Determine final origin X based on current page
        let finalOriginX = 50;
        if (window.innerWidth >= 768) {
          if (currentIndex === 0) finalOriginX = 75;
          else if (currentIndex + 1 >= totalPages) finalOriginX = 25;
        }

        // Animate back to normal scale
        // Set transition
        container.style.transformOrigin = `${finalOriginX}% 50%`;
        container.style.transform = `translateX(${restingTranslate}%) scale(1)`;

        if (zoomBtn) {
          zoomBtn.style.backgroundColor = "";
          zoomBtn.querySelector('.zoom-in-icon').style.display = 'block';
          zoomBtn.querySelector('.zoom-out-icon').style.display = 'none';
        }

        // After transition ends, reset styles
        setTimeout(() => {
          if (!isZoomed) {
            container.style.transition = 'none';
            container.style.transformOrigin = 'center center';
            container.style.transform = ''; // Reset to default

            if (window.innerWidth >= 768) {
              if (currentIndex === 0) container.classList.add('fbm-is-cover');
              else if (currentIndex + 1 >= totalPages) container.classList.add('fbm-is-back-cover');
            }
            // Force Reflow
            container.offsetHeight;
            container.style.transition = '';
          }
        }, 500);
      }
    }

    // --- PANNING LOGIC ---
    renderArea.addEventListener('mousemove', (e) => {
      if (!isZoomed) return;
      const container = section.querySelector('.fbm-canvas-container');
      if (!container) return;

      const rect = renderArea.getBoundingClientRect();

      //Take the mouse position and give it a little 'smoothing'
      let xPercent = ((e.clientX - rect.left) / rect.width) * 100;
      let yPercent = ((e.clientY - rect.top) / rect.height) * 100;

      // Clamp between 0% and 100%
      xPercent = Math.max(0, Math.min(100, xPercent));
      yPercent = Math.max(0, Math.min(100, yPercent));

      requestAnimationFrame(() => {
        container.style.transformOrigin = `${xPercent}% ${yPercent}%`;
      });
    });

    // --- PINCH-TO-ZOOM LOGIC (touch) ---
    let pinchStartDist = 0;
    let pinchActive = false;

    function touchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    renderArea.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        pinchActive = true;
        pinchStartDist = touchDistance(e.touches);
      }
    }, { passive: true });

    // Coalesced into requestAnimationFrame like the mousemove handler above -
    // touchmove can fire well over 60 times/sec, and writing style.transform
    // synchronously on every single event (as this did before) forces a
    // style recalc each time, which is exactly the kind of jank that shows
    // up on lower-end phones - the only devices this handler runs on.
    let pinchMoveRAF = null;
    renderArea.addEventListener('touchmove', (e) => {
      if (!pinchActive || e.touches.length !== 2 || !pageFlip) return;
      const container = section.querySelector('.fbm-canvas-container');
      if (!container) return;

      const dist = touchDistance(e.touches);
      const scaleDelta = dist / pinchStartDist;

      const rect = renderArea.getBoundingClientRect();
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      const xPercent = Math.max(0, Math.min(100, ((midX - rect.left) / rect.width) * 100));
      const yPercent = Math.max(0, Math.min(100, ((midY - rect.top) / rect.height) * 100));

      if (pinchMoveRAF) cancelAnimationFrame(pinchMoveRAF);
      pinchMoveRAF = requestAnimationFrame(() => {
        pinchMoveRAF = null;
        if (scaleDelta > 1.15 && !isZoomed) {
          renderArea.classList.add('is-zoomed');
          container.style.transition = 'none';
          container.style.transformOrigin = `${xPercent}% ${yPercent}%`;
          container.style.transform = `scale(${Math.min(scaleDelta, zoomScale)})`;
          isZoomed = true;
          stopAutoplay();
        } else if (isZoomed) {
          container.style.transformOrigin = `${xPercent}% ${yPercent}%`;
          container.style.transform = `scale(${Math.max(1, Math.min(scaleDelta, zoomScale))})`;
        }
      });
    }, { passive: true });

    renderArea.addEventListener('touchend', (e) => {
      if (!pinchActive) return;
      pinchActive = false;
      if (e.touches.length === 0 && !isZoomed) {
        toggleZoom(false);
      }
    });

    // --- NAVIGATION & ACTIONS ---
    section.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;

      const action = btn.dataset.action;
      if (!pageFlip && !['zoom', 'search', 'jumptopage'].includes(action)) return; // Prevent actions if not loaded

      const totalPages = pageMap.length;

      if (action === 'fullscreen') {
        toggleFullscreen();
      } else if (action === 'zoom') {
        toggleZoom();
      } else if (action === 'thumbnails') {
        thumbOverlay.hidden = !thumbOverlay.hidden;
        if (!thumbOverlay.hidden) generateThumbnails(); // lazy: only once actually opened
      } else if (action === 'next') {
        pageFlip.flipNext();
      } else if (action === 'prev') {
        pageFlip.flipPrev();
      } else if (action === 'first') {
        pageFlip.flip(0);
      } else if (action === 'last') {
        pageFlip.flip(totalPages - 1);
      } else if (action === 'autoplay') {
        toggleAutoplay(btn);
      } else if (action === 'share') {
        sharePage(btn);
        pushAnalyticsEvent({ action: 'share_open', catalog: catalogLabel });
      } else if (action === 'download') {
        downloadFiles(btn);
      } else if (action === 'print') {
        printFile(btn);
      } else if (action === 'jumptopage') {
        openJumpMenu(btn);
      } else if (action === 'search') {
        openSearchMenu(btn);
      }
    });

    viewport.addEventListener('dblclick', () => toggleZoom());

    // Helper Function: Render pages from PDF collection
    async function getSpecificPage(globalPageNum) {
      const mapping = pageMap[globalPageNum - 1];
      if (!mapping) return null;
      return await pdfDocs[mapping.docIndex].getPage(mapping.localPageIndex);
    }

    // Reset Function: Completely delete old containers and create new ones
    function rebuildContainer() {
      if (pageFlip) {
        try { pageFlip.destroy(); } catch(e) {}
        pageFlip = null;
      }

      // Clean viewport
      viewport.innerHTML = '';

      // Create a new container with a unique ID (using timestamp) to make it fresh
      const newContainer = document.createElement('div');
      newContainer.id = 'canvas-container-' + Date.now();
      newContainer.className = 'fbm-canvas-container';
      viewport.appendChild(newContainer);

      return newContainer;
    }

    // Scale the actual render resolution to the box the page is displayed
    // at (times devicePixelRatio) instead of a fixed 1.5/2 regardless of
    // how large the flipbook actually renders on screen. Capped lower on
    // detected low-end devices - every extra pixel here is real decode +
    // paint cost paid on every single page render, and an old phone's CPU
    // pays it in real, felt time.
    function computeRenderScale() {
      const cap = isLowEndDevice ? 1.6 : 2.5;
      if (!pdfPageWidthPts || !currentBW) return isLowEndDevice ? 1.4 : 2;
      const dpr = window.devicePixelRatio || 1;
      const scale = (currentBW / pdfPageWidthPts) * dpr;
      return Math.min(Math.max(scale, 1), cap);
    }

    async function loadPdf(urls) {
      if (!urls || urls.length === 0) {
        setLoaderState('error', 'No files selected.');
        return;
      }

      try {
        setLoaderState('loading', urls.length > 1 ? `Loading document 0/${urls.length}...` : 'Loading...');
        viewport.style.opacity = '0';

        // Rebuild container
        const canvasContainer = rebuildContainer();

        pdfDocs = [];
        pageMap = [];
        thumbsGenerated = false; // stale after a rebuild - new pages, new canvasContainer

        // Load every PDF in the block in parallel instead of one at a
        // time - with up to 8 files per block this alone can cut load
        // time substantially.
        let loadedCount = 0;
        const results = await Promise.all(urls.map(async (url, i) => {
          try {
            const loadingTask = pdfjsLib.getDocument({ url, disableAutoFetch: true });
            const doc = await loadingTask.promise;
            loadedCount++;
            if (urls.length > 1) setLoaderState('loading', `Loading document ${loadedCount}/${urls.length}...`);
            return { i, doc };
          } catch (e) {
            loadedCount++;
            console.error("Error loading:", e);
            return { i, doc: null };
          }
        }));
        results.sort((a, b) => a.i - b.i);

        let totalGlobalPages = 0;
        for (const { doc } of results) {
          if (!doc) continue;
          pdfDocs.push(doc);
          const docIndex = pdfDocs.length - 1; // always matches pdfDocs, even if an earlier url failed
          for (let p = 1; p <= doc.numPages; p++) {
            pageMap.push({ docIndex, localPageIndex: p });
            totalGlobalPages++;
          }
        }

        if (totalGlobalPages === 0) throw new Error("No pages found");

        section.querySelector('[data-total-pages]').textContent = totalGlobalPages;

        const currentPageDisplay = section.querySelector('[data-current-page]');
        if (currentPageDisplay) {
          currentPageDisplay.textContent = 1;
        }

        const firstPage = await getSpecificPage(1);
        const originalVp = firstPage.getViewport({ scale: 1 });
        const ratio = originalVp.width / originalVp.height;
        pdfPageRatio = ratio;
        pdfPageWidthPts = originalVp.width;

        // Wait one paint cycle instead of a blind 150ms guess, so the
        // container's real dimensions are read once layout has settled.
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

        const isMobile = window.innerWidth < 768;
        lastIsMobile = isMobile;
        const vW = renderArea.clientWidth - 20;
        const vH = renderArea.clientHeight - 20;

        if (vW <= 0 || vH <= 0) return;

        let bH, bW;
        if (isMobile) {
          bW = vW;
          bH = bW / ratio;
          if (bH > vH) {
            bH = vH;
            bW = bH * ratio;
          }
        } else {
          bH = vH;
          bW = bH * ratio;
          if (bW * 2 > vW) {
            bW = vW / 2;
            bH = bW / ratio;
          }
        }
        currentBW = bW;

        // Batch page-placeholder creation into a single DOM insertion
        // instead of one appendChild per page (matters for thick books).
        // Collected directly into pageDivs while creating them, instead of
        // querying the DOM for them again afterward.
        const fragment = document.createDocumentFragment();
        pageDivs = [];
        for (let i = 1; i <= totalGlobalPages; i++) {
          const div = document.createElement('div');
          div.className = 'fbm-page';
          div.style.width = Math.round(bW) + 'px';
          div.style.height = Math.round(bH) + 'px';
          fragment.appendChild(div);
          pageDivs.push(div);
        }
        canvasContainer.appendChild(fragment);

        // Initialize PageFlip
        // Shadow rendering during the flip animation is a real, repeated
        // compositing cost paid on every single flip - skipped on detected
        // low-end devices. Shorter flippingTime there too: fewer total
        // frames needed to complete the transition means less time spent
        // watching a stuttery animation even if individual frames drop.
        pageFlip = new St.PageFlip(canvasContainer, {
          width: Math.round(bW),
          height: Math.round(bH),
          size: "fixed",
          showCover: true,
          drawShadow: !isLowEndDevice,
          flippingTime: isLowEndDevice ? 500 : 800,
          maxShadowOpacity: isLowEndDevice ? 0 : 0.5,
          showPageCorners: true,
          mobileScrollSupport: false,
          usePortrait: isMobile,
          startPage: 0,
          autoSize: true
        });

        pageFlip.loadFromHTML(pageDivs);

        if (!isMobile) {
          canvasContainer.classList.add('fbm-is-cover');
        }

        const storageKey = 'fbm_last_page_' + sectionId + '_' + (catalogLabel || 'default');

        // Page Flip Event
        pageFlip.on('flip', (e) => {
          const currentPageIndex = e.data;
          const totalPages = pageMap.length;
          const displayElem = section.querySelector('[data-current-page]');

          let displayString = "";

          if (window.innerWidth < 768) {
            //Mobile Mode: Single Logic (1, 2, 3... 165)
            displayString = currentPageIndex + 1;
          } else {
            // Desktop Mode: Spread Logic (1, 2-3, 4-5... 164)
            if (currentPageIndex === 0) {
              displayString = "1"; // Front Cover
            } else if (currentPageIndex + 1 >= totalPages) {
              displayString = totalPages; // Back Cover
            } else {
              const pageLeft = currentPageIndex + 1;
              const pageRight = currentPageIndex + 2;
              displayString = (pageRight <= totalPages) ? `${pageLeft}-${pageRight}` : `${pageLeft}`;
            }
          }

          if (displayElem) displayElem.textContent = displayString;

          // Pre-render surrounding pages
          renderAround(currentPageIndex + 1);

          // Desktop Centering Logic
          if (window.innerWidth >= 768) {
            canvasContainer.classList.remove('fbm-is-cover', 'fbm-is-back-cover');
            if (currentPageIndex === 0) {
              canvasContainer.classList.add('fbm-is-cover');
            } else if (currentPageIndex + 1 >= totalPages) {
              canvasContainer.classList.add('fbm-is-back-cover');
            }
          }
          if (isZoomed) {
            toggleZoom(false);
          }

          try { localStorage.setItem(storageKey, String(currentPageIndex)); } catch (e) {}

          pushAnalyticsEvent({
            action: 'page_flip',
            catalog: catalogLabel,
            page: currentPageIndex + 1,
            total_pages: totalPages
          });
        });

        // Resume where the reader left off in this catalog, if we have it.
        let resumeIndex = 0;
        try {
          const saved = parseInt(localStorage.getItem(storageKey), 10);
          if (!isNaN(saved) && saved > 0 && saved < totalGlobalPages) resumeIndex = saved;
        } catch (e) {}

        if (resumeIndex > 0) {
          if (typeof pageFlip.turnToPage === 'function') pageFlip.turnToPage(resumeIndex);
          else pageFlip.flip(resumeIndex);
        }

        // Initial Render (around the resumed page, or the start if none)
        await renderPage(resumeIndex + 1);
        if (resumeIndex + 2 <= totalGlobalPages) await renderPage(resumeIndex + 2);

        loader.style.display = 'none';
        viewport.style.opacity = '1';

        pushAnalyticsEvent({
          action: 'flipbook_loaded',
          catalog: catalogLabel,
          total_pages: totalGlobalPages
        });

        // Warm up the buffer around the start in the background so the
        // very first flip (before any 'flip' event has ever fired to
        // trigger renderAround) doesn't land on an unrendered, blank page.
        renderAround(resumeIndex + 1).catch((e) => console.warn('Prerender skipped', e));

        // Thumbnails are generated lazily, the first time the Thumbnails
        // button is actually clicked (see the 'thumbnails' action below) -
        // not here. Rendering a canvas for every one of up to 140 pages
        // right after load spent real CPU/battery for readers who never
        // open that panel at all, which is the common case for a linear
        // read-through.

      } catch (err) {
        console.error("Flipbook Load Error:", err);
        setLoaderState('error', 'Failed to load flipbook.');
      }
    }

    // Lightweight resize: reuse already-fetched PDFs and just resize the
    // page boxes (the canvas inside scales via CSS width/height:100%
    // already). A full reload is only triggered from the resize listener
    // when the mobile/desktop breakpoint is actually crossed, since that's
    // the only case where PageFlip's layout mode itself has to change.
    function resizeContainer() {
      if (!pageFlip || !pdfPageRatio) return;
      const canvasContainer = section.querySelector('.fbm-canvas-container');
      if (!canvasContainer) return;

      const isMobile = window.innerWidth < 768;
      const vW = renderArea.clientWidth - 20;
      const vH = renderArea.clientHeight - 20;
      if (vW <= 0 || vH <= 0) return;

      let bH, bW;
      if (isMobile) {
        bW = vW;
        bH = bW / pdfPageRatio;
        if (bH > vH) { bH = vH; bW = bH * pdfPageRatio; }
      } else {
        bH = vH;
        bW = bH * pdfPageRatio;
        if (bW * 2 > vW) { bW = vW / 2; bH = bW / pdfPageRatio; }
      }
      currentBW = bW;

      pageDivs.forEach((div) => {
        div.style.width = Math.round(bW) + 'px';
        div.style.height = Math.round(bH) + 'px';
      });
    }

    async function renderPage(num) {
      const target = pageDivs[num - 1];
      if (!target || target.getAttribute('data-rendered') === 'true') return;

      const scale = computeRenderScale();
      const cacheKey = activePdfUrls.join(',') + '|' + num + '|' + Math.round(scale * 100);

      // Cache lookup runs alongside getSpecificPage() instead of after it,
      // so a cold cache (first-ever visit) never pays extra latency for
      // this - both are awaited together either way.
      const [page, cachedBlob] = await Promise.all([
        getSpecificPage(num),
        getCachedPageBitmap(cacheKey)
      ]);
      if (!page) return;

      let canvas = null;
      if (cachedBlob) {
        try {
          const bitmap = await createImageBitmap(cachedBlob);
          canvas = document.createElement('canvas');
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          canvas.getContext('2d').drawImage(bitmap, 0, 0);
          bitmap.close();
        } catch (e) {
          canvas = null; // corrupt/unreadable entry - fall through to a normal render
        }
      }

      if (!canvas) {
        const vp = page.getViewport({ scale });
        canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = vp.width;
        canvas.height = vp.height;

        await page.render({ canvasContext: ctx, viewport: vp }).promise;

        // Cache for next visit - fire and forget, never delays showing
        // the page. JPEG: these are photographic catalog pages, where it
        // compresses far smaller than PNG for a negligible quality cost.
        canvas.toBlob((blob) => {
          if (blob) setCachedPageBitmap(cacheKey, blob);
        }, 'image/jpeg', 0.85);
      }

      // Canvas (and the text layer below) live in an inner wrapper, not
      // directly on .fbm-page - .fbm-page itself is 3D-transformed by the
      // page-flip library during the flip animation, and it needs to stay
      // untouched (no position/stacking context of its own) for that to
      // render correctly.
      const inner = document.createElement('div');
      inner.className = 'fbm-page-inner';
      inner.appendChild(canvas);

      target.innerHTML = '';
      target.appendChild(inner);
      target.setAttribute('data-rendered', 'true');

      // Text layer is an accessibility/search/SEO enhancement (selectable,
      // screen-reader-visible text instead of pixels-only canvas) - fire
      // and forget so it never delays the visible page render. attachTextLayer
      // bails out on its own if the page turns out to have no extractable
      // text (scanned/image-only), so nothing upstream needs to pre-check
      // that on the critical load path.
      attachTextLayer(page, inner).catch((e) => console.warn('Text layer skipped for page', num, e));
    }

    async function attachTextLayer(page, inner) {
      if (!pdfjsLib.renderTextLayer || skipTextLayerForCatalog) return;
      const textContent = await page.getTextContent();
      const hasText = textContent.items.some(item => item.str && item.str.trim().length > 0);
      if (!hasText) {
        consecutiveTextlessPages++;
        if (consecutiveTextlessPages >= 3) skipTextLayerForCatalog = true;
        return; // scanned/image-only page - nothing to overlay
      }
      consecutiveTextlessPages = 0;
      const displayScale = (currentBW && pdfPageWidthPts) ? (currentBW / pdfPageWidthPts) : computeRenderScale();
      const displayVp = page.getViewport({ scale: displayScale });
      const textLayerDiv = document.createElement('div');
      textLayerDiv.className = 'textLayer';
      textLayerDiv.style.width = Math.round(displayVp.width) + 'px';
      textLayerDiv.style.height = Math.round(displayVp.height) + 'px';
      inner.appendChild(textLayerDiv);
      await pdfjsLib.renderTextLayer({
        textContentSource: textContent,
        textContent: textContent,
        container: textLayerDiv,
        viewport: displayVp
      }).promise;
    }

    // Wider than the visible spread on purpose: renderPage() is async (a
    // real decode+paint cost) while the flip animation itself is a fixed
    // 800ms CSS transition that starts as soon as the 'flip' event fires -
    // if a page's canvas hasn't at least STARTED rendering by then, the
    // reveal shows blank/white until the render catches up. A generous
    // buffer in both directions means most flips land on a page that was
    // already rendered while the reader was on a nearby page, not one
    // whose render only began the moment they arrived at it.
    // Narrower on mobile: each rendered page is an uncached, never-evicted
    // canvas bitmap that stays in memory for the rest of the session, and
    // phones both have less RAM to spend on that and are more likely to be
    // on a metered/slow connection paying for pages the reader may never
    // reach. Desktop can afford to spend more to stay ahead of fast
    // flipping; mobile trades a little of that smoothness back for a
    // lighter footprint. Narrower still on detected low-end mobile - the
    // CPU cost of decoding+painting each extra page ahead is the more
    // pressing constraint there than staying maximally ahead of the flip.
    async function renderAround(num) {
      const total = pageMap.length;
      const isMobile = window.innerWidth < 768;
      const lowEndMobile = isMobile && isLowEndDevice;
      const behind = lowEndMobile ? 1 : (isMobile ? 1 : 2);
      const ahead = lowEndMobile ? 1 : (isMobile ? 2 : 5);
      for (let i = num - behind; i <= num + ahead; i++) {
        if (i >= 1 && i <= total) await renderPage(i);
      }
      evictFarPages(num, isMobile);
    }

    // Rendered pages are never freed on their own - without this, reading
    // a 140-page catalog start to finish keeps every single page's canvas
    // bitmap in memory at once. Frees anything far outside the current
    // reading position and lets it lazily re-render if revisited (cheap:
    // pdf.js still has the parsed page object cached internally, only the
    // canvas paint has to redo). The keep radius is well beyond the
    // render-ahead buffer above so ordinary back-and-forth flipping near
    // the edge doesn't cause evict/re-render thrashing - it only kicks in
    // once the reader has actually moved on. Tighter on mobile, where RAM
    // is the scarcer resource, tighter still on detected low-end mobile.
    function evictFarPages(num, isMobile) {
      const keepRadius = isMobile ? (isLowEndDevice ? 4 : 8) : 20;
      pageDivs.forEach((div, idx) => {
        const pageNum = idx + 1;
        if (Math.abs(pageNum - num) > keepRadius && div.getAttribute('data-rendered') === 'true') {
          div.innerHTML = '';
          div.removeAttribute('data-rendered');
        }
      });
    }

    // --- STOP AUTOPLAY FUNCTION ---
    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
      isAutoplay = false;
      const btn = section.querySelector('[data-action="autoplay"]');
      if (btn) {
        btn.querySelector('.play-icon').style.display = 'block';
        btn.querySelector('.pause-icon').style.display = 'none';
        btn.classList.remove('is-playing');
      }
    }

    // --- THUMBNAILS LOGIC ---
    let isGeneratingThumbs = false;
    // Set once thumbnails exist for the CURRENT load, reset on every fresh
    // loadPdf() (new canvasContainer, new pages - old thumbnails point at
    // stale page objects). Without this, opening the panel, closing it,
    // and reopening it would re-render all ~140 thumbnails from scratch
    // every single time.
    let thumbsGenerated = false;

    async function generateThumbnails() {
      if (pdfDocs.length === 0 || isGeneratingThumbs || thumbsGenerated) return;

      isGeneratingThumbs = true;
      thumbGrid.innerHTML = '';
      const total = pageMap.length;

      // Detect Mobile or Desktop
      const isMobileView = window.innerWidth < 768;

      // STEP A: Determine the order of pages by mode (Mobile vs Desktop)
      const thumbTasks = [];

      if (isMobileView) {
        // MOBILE MODE: Show single pages (1, 2, 3...)
        for (let i = 1; i <= total; i++) {
          thumbTasks.push({ pages: [i], label: i.toString() });
        }
      } else {
        // DESKTOP MODE: Show pairs (1, 2-3, 4-5...)
        thumbTasks.push({ pages: [1], label: "1" });
        for (let i = 2; i <= total; i += 2) {
          const pages = [i];
          if (i + 1 <= total) pages.push(i + 1);
          const labelText = pages.length > 1 ? `${pages[0]}-${pages[1]}` : `${pages[0]}`;
          thumbTasks.push({ pages, label: labelText });
        }
      }

      // STEP B: Create HTML Elements (Synchronous)
      const taskElements = thumbTasks.map(task => {
        const item = document.createElement('div');
        item.className = 'fbm-thumb-item';

        const wrapper = document.createElement('div');
        wrapper.className = 'fbm-thumb-image-wrapper';
        wrapper.innerHTML = '<div style="font-size:10px; opacity:0.5; padding-top:20px;">Loading...</div>';

        const label = document.createElement('div');
        label.className = 'fbm-thumb-label';
        label.textContent = task.label;

        item.appendChild(wrapper);
        item.appendChild(label);

        item.addEventListener('click', (e) => {
          e.stopPropagation();
          pageFlip.flip(task.pages[0] - 1);
          thumbOverlay.hidden = true;
        });

        thumbGrid.appendChild(item);
        return { wrapper, pages: task.pages };
      });

      // STEP C: Render Images (Asynchronous)
      for (const task of taskElements) {
        try {
          const canvas = await renderThumbCanvas(task.pages);
          task.wrapper.innerHTML = '';
          task.wrapper.appendChild(canvas);
        } catch (err) {
          console.error("Error rendering thumbnail:", err);
          task.wrapper.innerHTML = '<div style="color:red">!</div>';
        }
      }

      thumbsGenerated = true;
      isGeneratingThumbs = false;
    }

    // The renderThumbCanvas function does not need to be changed,
    // because he will automatically render 1 page if the contents of the array 'pages' are only one.
    async function renderThumbCanvas(pageNums) {
      const isSpread = pageNums.length > 1;
      const page1 = await getSpecificPage(pageNums[0]);
      const vp1 = page1.getViewport({ scale: 0.15 });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (isSpread) {
        const page2 = await getSpecificPage(pageNums[1]);
        const vp2 = page2.getViewport({ scale: 0.15 });

        canvas.width = vp1.width + vp2.width;
        canvas.height = Math.max(vp1.height, vp2.height);

        await page1.render({ canvasContext: ctx, viewport: vp1 }).promise;
        ctx.translate(vp1.width, 0);
        await page2.render({ canvasContext: ctx, viewport: vp2 }).promise;
      } else {
        // Automatically log in here if Mobile (because task.pages only [1], [2], etc)
        canvas.width = vp1.width;
        canvas.height = vp1.height;
        await page1.render({ canvasContext: ctx, viewport: vp1 }).promise;
      }
      return canvas;
    }

    // --- MERGE HELPER (shared by Download and Print) ---
    async function mergeActivePdfs() {
      await window.__fbmLoader.loadPdfLib(); // pdf-lib is loaded on demand, not upfront
      const { PDFDocument } = PDFLib;
      const mergedPdf = await PDFDocument.create();

      for (const url of activePdfUrls) {
        const bytes = await fetch(url).then(res => res.arrayBuffer());
        const pdf = await PDFDocument.load(bytes);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      const mergedPdfBytes = await mergedPdf.save();
      return new Blob([mergedPdfBytes], { type: 'application/pdf' });
    }

    function currentFileName() {
      const activeTab = section.querySelector('.fbm-pdf-tab.is-active');
      const currentTabLabel = activeTab ? activeTab.innerText.trim() : "Catalogue";
      return (currentTabLabel + " Catalogue").replace(/[/\\?%*:|"<>]/g, '-');
    }

    // --- DOWNLOAD LOGIC (MERGE PDF + DYNAMIC FILENAME FROM TAB) ---
    async function downloadFiles(btn) {
      if (activePdfUrls.length === 0) return;

      const sanitizedFileName = currentFileName();

      const originalContent = btn.innerHTML;
      btn.innerHTML = '<span>Processing...</span>';
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.7';

      try {
        const blob = await mergeActivePdfs();
        const downloadUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = sanitizedFileName + ".pdf";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);

        pushAnalyticsEvent({ action: 'download', catalog: catalogLabel, file_name: sanitizedFileName });

      } catch (err) {
        console.error("The merge process failed:", err);
        window.open(activePdfUrls[0], '_blank');
      } finally {
        btn.innerHTML = originalContent;
        btn.style.pointerEvents = 'auto';
        btn.style.opacity = '1';
      }
    }

    // --- PRINT LOGIC ---
    async function printFile(btn) {
      if (activePdfUrls.length === 0) return;

      const originalContent = btn.innerHTML;
      btn.innerHTML = '<span>Preparing...</span>';
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.7';

      try {
        const blob = await mergeActivePdfs();
        const blobUrl = URL.createObjectURL(blob);

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.src = blobUrl;
        document.body.appendChild(iframe);

        iframe.onload = () => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            window.open(blobUrl, '_blank');
          }
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(blobUrl);
          }, 60000);
        };

        pushAnalyticsEvent({ action: 'print', catalog: catalogLabel });

      } catch (err) {
        console.error("Print failed:", err);
        window.open(activePdfUrls[0], '_blank');
      } finally {
        btn.innerHTML = originalContent;
        btn.style.pointerEvents = 'auto';
        btn.style.opacity = '1';
      }
    }

    // --- SHARE LOGIC ---
    function sharePage(btn) {
      if (!shareMenu) return;
      const isOpen = !shareMenu.hasAttribute('hidden');
      if (isOpen) {
        shareMenu.hidden = true;
        return;
      }
      if (jumpMenu) jumpMenu.hidden = true;
      if (searchMenu) searchMenu.hidden = true;

      const container = section.querySelector('.fbm-main-container');
      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const xPosition = (btnRect.left - containerRect.left) + (btnRect.width / 2);

      shareMenu.style.left = xPosition + 'px';
      shareMenu.hidden = false;
    }

    shareMenu.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-share]');
      if (!btn) return;
      const url = encodeURIComponent(window.location.href);
      const title = encodeURIComponent(document.title);

      const map = {
          x: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
          facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
          whatsapp: `https://wa.me/?text=${url}`,
          telegram: `https://t.me/share/url?url=${url}`
        };

        if (map[btn.dataset.share]) {
          window.open(map[btn.dataset.share], '_blank');
        } else {
          navigator.clipboard.writeText(location.href);
          alert('Link copied!');
        }
      shareMenu.hidden = true;
    });

    // --- JUMP TO PAGE LOGIC ---
    function openJumpMenu(btn) {
      if (!jumpMenu) return;
      const isOpen = !jumpMenu.hasAttribute('hidden');
      if (isOpen) {
        jumpMenu.hidden = true;
        return;
      }
      if (shareMenu) shareMenu.hidden = true;
      if (searchMenu) searchMenu.hidden = true;
      jumpMenu.hidden = false;
      if (jumpInput) {
        jumpInput.max = pageMap.length || '';
        jumpInput.focus();
      }
    }

    function doJump() {
      if (!pageFlip || !jumpInput) return;
      const num = parseInt(jumpInput.value, 10);
      const total = pageMap.length;
      if (!num || num < 1 || num > total) return;
      pageFlip.flip(num - 1);
      jumpMenu.hidden = true;
      jumpInput.value = '';
    }

    if (jumpGoBtn) jumpGoBtn.addEventListener('click', doJump);
    if (jumpInput) {
      jumpInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') doJump();
      });
    }

    // --- SEARCH LOGIC (across the whole book, not just rendered pages) ---
    async function getPageText(num) {
      if (pageTextCache[num] !== undefined) return pageTextCache[num];
      const page = await getSpecificPage(num);
      if (!page) return '';
      const content = await page.getTextContent();
      const text = content.items.map(item => item.str).join(' ');
      pageTextCache[num] = text;
      return text;
    }

    function openSearchMenu(btn) {
      if (!searchMenu) return;
      const isOpen = !searchMenu.hasAttribute('hidden');
      if (isOpen) {
        searchMenu.hidden = true;
        return;
      }
      if (shareMenu) shareMenu.hidden = true;
      if (jumpMenu) jumpMenu.hidden = true;
      searchMenu.hidden = false;
      if (searchInput) searchInput.focus();
    }

    async function runSearch(query) {
      const myToken = ++searchToken;
      if (!query || !pageMap.length) {
        searchMatches = [];
        searchMatchIndex = -1;
        if (searchCount) searchCount.textContent = '0/0';
        return;
      }
      if (searchCount) searchCount.textContent = '...';
      const q = query.toLowerCase();
      const total = pageMap.length;
      const matches = [];
      for (let i = 1; i <= total; i++) {
        const text = await getPageText(i);
        if (myToken !== searchToken) return; // a newer search superseded this one
        if (text.toLowerCase().includes(q)) matches.push(i);
      }
      if (myToken !== searchToken) return;
      searchMatches = matches;
      searchMatchIndex = matches.length ? 0 : -1;
      if (searchCount) searchCount.textContent = `${matches.length ? 1 : 0}/${matches.length}`;
      if (matches.length) pageFlip.flip(matches[0] - 1);
    }

    function goToMatch(direction) {
      if (!searchMatches.length) return;
      searchMatchIndex = (searchMatchIndex + direction + searchMatches.length) % searchMatches.length;
      if (searchCount) searchCount.textContent = `${searchMatchIndex + 1}/${searchMatches.length}`;
      pageFlip.flip(searchMatches[searchMatchIndex] - 1);
    }

    if (searchInput) {
      let searchDebounce = null;
      searchInput.addEventListener('input', () => {
        clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => runSearch(searchInput.value.trim()), 400);
      });
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') goToMatch(1);
      });
    }
    const searchPrevBtn = section.querySelector('.fbm-search-prev');
    const searchNextBtn = section.querySelector('.fbm-search-next');
    const searchCloseBtn = section.querySelector('.fbm-search-close');
    if (searchPrevBtn) searchPrevBtn.addEventListener('click', () => goToMatch(-1));
    if (searchNextBtn) searchNextBtn.addEventListener('click', () => goToMatch(1));
    if (searchCloseBtn) searchCloseBtn.addEventListener('click', () => { searchMenu.hidden = true; });

    document.addEventListener('click', (e) => {
      if (shareMenu && !shareMenu.contains(e.target) && !e.target.closest('[data-action="share"]')) {
        shareMenu.hidden = true;
      }
      if (jumpMenu && !jumpMenu.contains(e.target) && !e.target.closest('[data-action="jumptopage"]')) {
        jumpMenu.hidden = true;
      }
      if (searchMenu && !searchMenu.contains(e.target) && !e.target.closest('[data-action="search"]')) {
        searchMenu.hidden = true;
      }
    });

    function toggleAutoplay(btn) {
      if (!pageFlip || pageMap.length === 0) return;

      if (isAutoplay) {
        stopAutoplay();
      } else {
        isAutoplay = true;
        btn.querySelector('.play-icon').style.display = 'none';
        btn.querySelector('.pause-icon').style.display = 'block';
        btn.classList.add('is-playing');

        autoplayTimer = setInterval(() => {
          const currentIndex = pageFlip.getCurrentPageIndex();
          const totalPages = pageMap.length;
          // STOP AUTOPLAY AT THE END
          if (currentIndex + 1 >= totalPages) {
            stopAutoplay(); // STOP IF REACHED THE END
          } else {
            pageFlip.flipNext(); // GO TO NEXT PAGE
          }
        }, 3000); // 3 seconds interval
      }
    }

    tabs.forEach(tab => {
      tab.addEventListener('click', function() {
        const urls = this.dataset.pdfUrls.split(',').filter(u => u.trim() !== "");
        if (JSON.stringify(urls) === JSON.stringify(activePdfUrls)) return;

        // --- STOP AUTOPLAY ON TAB SWITCH ---
        stopAutoplay();

        // Make sure here to use fbm-pdf-tab
        tabs.forEach(t => t.classList.remove('is-active'));
        this.classList.add('is-active');

        activePdfUrls = urls;
        catalogLabel = this.textContent.trim();
        Object.keys(pageTextCache).forEach(k => delete pageTextCache[k]);
        consecutiveTextlessPages = 0;
        skipTextLayerForCatalog = false;
        loadPdf(activePdfUrls);
      });
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      // ESC key to close overlay and zoom
      if (e.key === 'Escape') {
        thumbOverlay.hidden = true;
        if (shareMenu) shareMenu.hidden = true;
        if (jumpMenu) jumpMenu.hidden = true;
        if (searchMenu) searchMenu.hidden = true;
        if (isZoomed) toggleZoom(false);
      }

      // Arrow Navigation (Only works if the flipbook is ready)
      if (!pageFlip) return;

      // Check if the user is typing in the input (to avoid conflicts)
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) return;

      if (e.key === 'ArrowRight') {
        // Right Arrow -> Next Page
        pageFlip.flipNext();
      } else if (e.key === 'ArrowLeft') {
        // Left Arrow -> Previous Page
        pageFlip.flipPrev();
      }
    });

    // Close button → hide overlay
    if (thumbCloseBtn) thumbCloseBtn.onclick = () => thumbOverlay.hidden = true;

    // Click outside the grid → hide overlay
    document.addEventListener('click', (e) => {
      if (!thumbOverlay.hidden && !thumbOverlay.contains(e.target) && !e.target.closest('[data-action="thumbnails"]')) {
        thumbOverlay.hidden = true;
      }
    });

    // Resize: only a full reload when crossing the mobile/desktop
    // breakpoint (PageFlip's layout mode genuinely changes then).
    // Anything smaller - a scrollbar toggling, a few px of browser chrome -
    // just resizes the existing page boxes in place instead of refetching
    // and re-parsing every PDF in the block.
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(window.fbResizeTimeout);
      window.fbResizeTimeout = setTimeout(() => {
        if (suppressResizeReload) return; // a fullscreen transition is already handling this resize
        // Devtools opening/closing (or any resize) while ALREADY in
        // fullscreen doesn't fire fullscreenchange - re-apply the forced
        // size here too, or it'd keep using the stale pixel value from
        // whenever fullscreen was first entered.
        if (isThisSectionFullscreen) applyFullscreenLayout();
        const isMobileNow = window.innerWidth < 768;
        if (isMobileNow !== lastIsMobile) {
          loadPdf(activePdfUrls);
        } else {
          resizeContainer();
        }
      }, 300);
    });

    if (retryButton) {
      retryButton.addEventListener('click', () => {
        setLoaderState('loading', 'Loading...');
        window.__fbmLoader.loadCore().then(() => {
          pdfjsLib = window['pdfjs-dist/build/pdf'];
          loadPdf(activePdfUrls);
        }).catch(() => setLoaderState('error', 'Failed to load resources. Please try again later.'));
      });
    }

    // Initial Load: fetch the core libraries (shared/deduped across every
    // flipbook section on the page) before touching any PDFs.
    window.__fbmLoader.loadCore().then(() => {
      pdfjsLib = window['pdfjs-dist/build/pdf'];
      loadPdf(activePdfUrls);
    }).catch((err) => {
      console.error('Failed to load flipbook libraries:', err);
      setLoaderState('error', 'Failed to load resources. Check your connection and try again.');
    });
  }

  // Only fetch the heavy libraries and initialize once the section is
  // actually about to be seen, instead of unconditionally on every page
  // load regardless of whether the visitor ever scrolls to it.
  function scheduleInit(sectionId) {
    const section = document.getElementById('fbm-' + sectionId);
    if (!section) return;

    if (!('IntersectionObserver' in window)) {
      initFlipbook(sectionId);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          initFlipbook(sectionId);
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0 });

    observer.observe(section);
  }

  // This file is a static asset (no Liquid processing), so the section id
  // can't be injected directly - auto-discover every v2 flipbook section
  // present on the page instead and schedule each one independently. The
  // .fbm-section-v2 marker keeps this scoped to v2 instances only, so it
  // never tries to initialize the older flipbook-magazine / -multi variants
  // (different markup, would error if this script drove them).
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.fbm-section-v2').forEach((el) => {
      const sectionId = el.id.replace(/^fbm-/, '');
      if (sectionId) scheduleInit(sectionId);
    });
  });
  // Shopify Theme Editor support
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target && e.target.querySelector && e.target.querySelector('.fbm-section-v2')) {
        initFlipbook(e.detail.sectionId);
      }
    });
  }
})();
